/* eslint-disable eqeqeq */
/* eslint-disable no-param-reassign */
/* eslint-disable arrow-body-style */
// @flow

import React, { useEffect } from 'react';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import Input from 'components/atoms/input/react/Input';
import { t, tp } from 'javascripts/utils/withTranslation';
import Label from 'components/atoms/label/react/Label';
import { searchAndMarkInArrayOfObjects } from 'javascripts/utils/text-match';
import Multiselect from 'components/organisms/multiselect/react/ControlledMultiselect';
import Button from 'components/atoms/button/Button';
import type { Option } from 'components/organisms/multiselect/react/ControlledMultiselect';
import { Accordion, AccordionItem } from 'components/organisms/accordion/react';
import type { Data, Item, Group } from '../types';

const getEnhancedGroups = (
  res: Item[],
  groups: { [key: string]: Group }
): { [key: string]: Group } => {
  const grouped: { [key: string]: Group } = {};
  res.forEach((item) => {
    if (item.group && groups[item.group]) {
      if (!grouped[item.group]) {
        grouped[item.group] = { ...groups[item.group], items: [] };
      }
      // $FlowFixMe
      grouped[item.group].items?.push(item);
    }
  });
  return grouped;
};

const AccordionSearch = ({
  items = [],
  groups = {},
}: Data): React$MixedElement => {
  const [searchValue, setSearchValue] = React.useState('');
  const [multiSelectValue, setMultiSelectValue] = React.useState<Option[]>([]);
  const [queryText, setQueryText] = React.useState('');
  const [queryGroup, setQueryGroup] = React.useState<Option[]>([]);
  const [results, setResults] = React.useState(items || []);
  const [closedAccordionItems, setClosedAccordionItems] = React.useState<
    string[]
  >(items.map((item) => item.id || ''));
  const groupOptions = React.useMemo(
    () =>
      Object.entries(groups || {})
        .map(([key, value]) => ({
          text: value.text || key,
          value: String(key),
          selected: false,
          ...value,
        }))
        .sort((a, b) => (a.order || 0) - (b.order || 0)),
    [groups]
  );

  const handleSearch = (e: any) => {
    e.preventDefault();
    setClosedAccordionItems([]);
    setQueryText(searchValue);
    setQueryGroup(multiSelectValue);
  };

  const handleClear = () => {
    setSearchValue('');
  };

  const toggleAccordionItem = (id?: string) => {
    if (!id) return;
    setClosedAccordionItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const isAccordionItemOpen = (id?: string) => {
    return !closedAccordionItems.includes(id);
  };

  useEffect(() => {
    if ((!queryText || !queryText.trim()) && queryGroup.length === 0) {
      setResults(items);
      return;
    }

    let queryItems = items;
    if (queryGroup.length > 0) {
      queryItems = items.filter(
        (item) => item.group && queryGroup.some((g) => g.value == item.group)
      );
    }

    if ((!queryText || !queryText.trim()) && queryGroup.length > 0) {
      setResults(queryItems);
      return;
    }

    const res = searchAndMarkInArrayOfObjects(
      queryItems,
      queryText,
      ['headline', 'bodytext', 'prefix'],
      {
        separateWordSearch: false,
      }
    );
    setResults(res);
  }, [queryText, queryGroup]);

  const handleMultiSelectChange = (newSelectedItems: Option[]) => {
    setMultiSelectValue(newSelectedItems);
  };

  return (
    <>
      <form
        className="form list-container__header accordion-search__form"
        role="search"
        onSubmit={handleSearch}
      >
        <div className="form__field">
          <div className="form-group">
            <Label htmlFor="accordion-search-input">
              {t('FAQ durchsuchen')}
            </Label>
            <div className="search__wrap">
              <Input
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                name="accordion-search-input"
                placeholder={t('Suchbegriff eingeben')}
                classNames="input--large"
                type="search"
              />
              {searchValue && searchValue.length > 0 && (
                <Button
                  icon="close"
                  style="icon"
                  onClick={handleClear}
                  className="button--large search__close"
                  ariaLabel={t('Eingabe löschen')}
                />
              )}
            </div>
          </div>
        </div>
        <div className="form__field form__field--auto">
          <Multiselect
            // $FlowFixMe
            options={groupOptions}
            selectedItems={multiSelectValue}
            setSelectedItems={(i) => handleMultiSelectChange(i)}
            large
            labelText={t('Themen')}
            placeholder={t('Alle Themen')}
          />
        </div>
        <div className="form__buttons form__buttons--right">
          <Button icon="search" type="submit" className="button--large">
            {t('Suchen')}
          </Button>
        </div>
      </form>

      <div className="list-container__content accordion-search__result">
        <div
          className="list-container__list-headline"
          aria-live="polite"
          style={
            queryText.length === 0 || !queryText
              ? { marginBottom: 0 }
              : undefined
          }
        >
          {queryText.length > 0 && (
            <p className="headline headline--6">
              {results.length}{' '}
              {tp('Eintrag gefunden', 'Einträge gefunden', results.length)}
            </p>
          )}
        </div>
        {Object.entries(getEnhancedGroups(results, groups))
          .sort((a, b) => (a[1].order || 0) - (b[1].order || 0))
          .map(([key, value]) => (
            <section key={key} className="accordion-search__group">
              <h2
                className={`headline headline--${
                  queryText ? '7' : '4'
                } accordion-search__group-title`}
              >
                {value.text}
              </h2>
              <Accordion className="accordion-search__items" appearance="grid">
                {value.items?.map((item) => (
                  <AccordionItem
                    key={item.id}
                    prefix={parse(DOMPurify.sanitize(item.prefix))}
                    headline={parse(DOMPurify.sanitize(item.headline))}
                    open={isAccordionItemOpen(item.id)}
                    onToggleOpen={() => toggleAccordionItem(item.id)}
                  >
                    {parse(DOMPurify.sanitize(item.bodytext))}
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          ))}
      </div>
    </>
  );
};

export default AccordionSearch;
